import CIMSTable from '../../..'
import { MutationType, QueryType, RPTQuickLinksTableProps } from './types'
import { useRestore } from './hooks/useRestore'
import { useColumns } from './hooks/useColumns'
import { useCreate } from './hooks/useCreate'
import { useRemove } from './hooks/useRemove'
import { useEdit } from './hooks/useEdit'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useGlobalState } from 'context/GlobalStateContext'

/**
 * Sets up a {@link CIMSTable} for entities which extend the RPTQuickLink class
 * on the server
 *
 * all you should need is a single index file and a graphql folder for the query
 * and associated mutations. No need to set up a context or anything more complex
 *
 * if the `mutation` property of each action is getting mad at you before you've
 * defined every action, that's why. The typing won't be happy until you define
 * each one and I'm not sure how to make it happy at this time.
 *
 * @see {@link RPTQuickLinksTableProps}
 * @see {@link RPTQuickLink}
 */
function RPTQuickLinksTable<
  Query extends QueryType,
  Create extends MutationType,
  Edit extends MutationType,
  Remove extends MutationType,
  Restore extends MutationType
>({
  entity,
  query,
  getQuickLinks,
  create,
  edit,
  remove,
  restore,
}: RPTQuickLinksTableProps<Query, Create, Edit, Remove, Restore>) {
  const {
    state: { projectGlobalId: equals },
  } = useGlobalState()
  const { data, loading, refetch } = query({
    ...DefaultQueryHookOptions,
    variables: { input: { projectId: { equals } } },
    skip: !equals,
  })
  return (
    <CIMSTable
      records={getQuickLinks(data)}
      rowKey="id"
      columns={useColumns({ entity })}
      loading={loading}
      actions={{
        header: {
          onReload: refetch,
          create: useCreate({ entity, refetch, ...create }),
        },
        row: {
          edit: useEdit({ entity, refetch, ...edit }),
          remove: useRemove({ refetch, ...remove }),
          restore: useRestore({ refetch, ...restore }),
        },
      }}
    />
  )
}

export default RPTQuickLinksTable
